import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "../../components/AdminLayout";
import { AdminFilterBar } from "../../components/admin/AdminFilterBar";
import { adminHeaders, api } from "../../lib/api";
import { shortDate } from "../../lib/format";
import { actionLabel, copyRawJson, formatAdminLogSummary, prettyRawJson } from "../../lib/adminDisplayLabels";
import { isLocalDevFrontend } from "../../lib/env";

interface LogRow {
  id?: number;
  action?: string;
  target_type?: string;
  target_id?: string | null;
  payload?: string | null;
  operator_id?: string | null;
  source_page?: string | null;
  note?: string | null;
  created_at?: string;
}

export function AdminAuditLogPage() {
  const isLocalDev = isLocalDevFrontend();
  const [rows, setRows] = useState<LogRow[]>([]);
  const [memberAccount, setMemberAccount] = useState("");
  const [actionType, setActionType] = useState("");
  const [operator, setOperator] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [copyMessage, setCopyMessage] = useState("");
  const [error, setError] = useState("");

  async function load() {
    setError("");
    try {
      setRows(await api<LogRow[]>("/api/admin/logs", { headers: adminHeaders() }));
    } catch (err) {
      setRows([]);
      setError(err instanceof Error ? err.message : "操作紀錄載入失敗");
    }
  }

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => rows.filter((row) => {
    const target = String(row.target_id ?? "");
    const payload = String(row.payload ?? "");
    const accountMatch = !memberAccount.trim() || target.includes(memberAccount.trim()) || payload.includes(memberAccount.trim());
    const actionMatch = !actionType.trim() || String(row.action ?? "").toLowerCase().includes(actionType.trim().toLowerCase());
    const operatorMatch = !operator.trim() || String(row.operator_id ?? "").toLowerCase().includes(operator.trim().toLowerCase());
    const date = String(row.created_at ?? "").slice(0, 10);
    const fromMatch = !dateFrom || date >= dateFrom;
    const toMatch = !dateTo || date <= dateTo;
    return accountMatch && actionMatch && operatorMatch && fromMatch && toMatch;
  }), [rows, memberAccount, actionType, operator, dateFrom, dateTo]);

  async function copy(row: LogRow) {
    await copyRawJson(row.payload ?? row);
    setCopyMessage(`已複製操作紀錄 #${row.id ?? "-"}`);
  }

  return (
    <AdminLayout title="操作紀錄">
      <section className="admin-card">
        <div className="admin-card-head">
          <span>LOG FILTERS</span>
          <h2>操作紀錄查詢</h2>
        </div>
        <AdminFilterBar>
          <label>
            會員帳號
            <input value={memberAccount} onChange={(event) => setMemberAccount(event.target.value)} placeholder={isLocalDev ? "test003" : "請輸入會員帳號"} />
          </label>
          <label>
            操作類型
            <input value={actionType} onChange={(event) => setActionType(event.target.value)} placeholder="建立派發 / create_reward" />
          </label>
          <label>
            開始日期
            <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label>
            結束日期
            <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>
          <label>
            操作人
            <input value={operator} onChange={(event) => setOperator(event.target.value)} placeholder="admin" />
          </label>
          <button onClick={load}>查詢</button>
          <button className="secondary" onClick={() => { setMemberAccount(""); setActionType(""); setDateFrom(""); setDateTo(""); setOperator(""); }}>重設</button>
        </AdminFilterBar>
      </section>

      {copyMessage ? <p className="result">{copyMessage}</p> : null}
      {error ? <p className="error-box">{error}</p> : null}

      <section className="admin-card">
        <div className="admin-card-head">
          <span>ADMIN LOGS</span>
          <h2>操作紀錄列表</h2>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>操作時間</th>
                <th>操作人</th>
                <th>目標</th>
                <th>操作類型</th>
                <th>操作內容</th>
                <th>原始資料</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, index) => (
                <tr key={row.id ?? index}>
                  <td>{shortDate(row.created_at)}</td>
                  <td>{row.operator_id ?? "admin"}</td>
                  <td>{row.target_id ?? row.target_type ?? "-"}</td>
                  <td>
                    <strong>{actionLabel(row.action)}</strong>
                    {row.action && actionLabel(row.action) === "未知操作" ? <small className="technical-key">{row.action}</small> : null}
                  </td>
                  <td>{formatAdminLogSummary(row)}</td>
                  <td>
                    <details className="raw-details">
                      <summary>查看原始資料</summary>
                      <pre>{prettyRawJson(row.payload ?? row)}</pre>
                    </details>
                    <button className="secondary mini-button" onClick={() => copy(row)}>複製原始資料</button>
                  </td>
                </tr>
              ))}
              {!filtered.length ? <tr><td colSpan={6}>目前沒有符合條件的操作紀錄。</td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>
    </AdminLayout>
  );
}
